// @flow

/*
   PathUtils: utils and constants for Paths (branching).  Localizing
   path concerns here so we can see what non-UI changes are being made
   for Paths. As the MVP and backend progresses, things will change
   here.
*/

import _ from 'lodash';
import memoize from 'memoize-weak';

import { PathColors, getPathColorValue } from '@zapier/editor';

import { isActivatableNode } from 'app/beta-editor/utils/NodeUtils';
import { isFilter } from 'app/service/utils';
import { nodeTypes } from 'app/service/constants';
import {
  findNodeUpwards,
  getChildren,
  getDescendants,
  getNodeAndDescendants,
  getParentId,
  getParentsToChildrenMap,
  getRootNode,
} from 'app/beta-editor/utils/NodeTrees';
import {
  getLastStepIsMiddleStepErrors,
  getStepIndex,
  getTriggerNode,
  isTriggerStep,
} from 'app/beta-editor/utils/FlowUtils';

import type {
  Flow,
  FlowNode,
  NodeMap,
  NodesSampleData,
  SkinnyNode,
} from 'app/beta-editor/flowState';
import type { ID } from 'app/common/types';
import type { ServicesMap } from 'app/service/types';

export const PATH_ACTION = 'filter';

// Info about one path
export type PathInfo = {|
  // The path name, either entered by user, or defaulted by us.
  name: string,

  // The color name, selected by user or defaulted
  color: string,

  // Emoji character, or single letter ie 'a'.
  emoji: string,
|};

/*
   The service API name of the backend Path app. Since paths do not
   have their own type_of value yet, we sometimes need to check the
   actual api name to determine if it is a path step.

   This constant is *not* exported, to discourage checking this value
   directly.  Util functions defined in this file should be used
   instead.
 */
export const PATH_API = 'BranchingAPI';

const BACKGROUND_OPACITY_LOW = 0.03;
const BACKGROUND_OPACITY_MED = 0.06;
const BACKGROUND_OPACITY_HIGH = 0.1;

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const isPathApi = (api?: string) => api === PATH_API;

export const isPathAction = (action: string) => action === PATH_ACTION;

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const isPathNode = (node: SkinnyNode) =>
  node &&
  isPathApi(node.selected_api) &&
  isFilter(node.type_of) &&
  isPathAction(node.action);

export const isPathsGroupType = (type: string) =>
  type === nodeTypes.PATHS_GROUP;

export const isPathsGroupNode = (node: SkinnyNode) => {
  return (
    node &&
    (isPathApi(node.selected_api) &&
      node.type_of === 'write' &&
      node.action === 'branch')
  );
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getPathName = (node: SkinnyNode): string => {
  const fallback = _.get(node, ['meta', 'stepTitle'], '');

  return _.get(node, ['params', 'name'], fallback);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getPathColor = (node: SkinnyNode): string =>
  _.get(node, ['params', 'color']);

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getPathEmoji = (node: SkinnyNode): string =>
  _.get(node, ['params', 'emoji']);

export const getPathInfo = (node: SkinnyNode): PathInfo => ({
  name: getPathName(node),
  color: getPathColor(node),
  emoji: getPathEmoji(node),
});

export const getAllPathNodesForZap = (nodes: NodeMap) => {
  return _.filter(nodes, isPathNode);
};

/*
   Given an opacity and a path color name, return a rgba function
   string, for use as a css style value.
*/
const getBackground = (opacity: number, color: string) => {
  const c = getPathColorValue(color);
  return `rgba(${c.value.join(', ')}, ${opacity})`;
};

/*
   Given a path color name, return normal background style value.
*/
export const getPathBackgroundColor = _.partial(
  getBackground,
  BACKGROUND_OPACITY_LOW
);

/*
   Given a path color name, return background style value for selected
   elements.
*/
export const getPathSelectedBackgroundColor = _.partial(
  getBackground,
  BACKGROUND_OPACITY_MED
);

export const getPathBackgroundColorHighOpacity = _.partial(
  getBackground,
  BACKGROUND_OPACITY_HIGH
);

const makeLetter = (letter, offset) =>
  String.fromCharCode(letter.charCodeAt(0) + offset);

const nextLetter = (letter, numUsed) => {
  if (!letter) {
    return 'A';
  }
  // if we've gone thru the whole alphabet, just start recycling letters
  if (numUsed >= 26) {
    return makeLetter('A', numUsed % 26);
  }
  return makeLetter(letter, 1);
};

/*
   Get the next set of initial values to use for the next path created
   in a zap. We cycle thru the list of emojis and colors to provide
   the default values - 'Path A', 'Path B', etc.

   Finds the next unused value, to handle cases where path nodes are
   added and then deleted from a zap, resulting in 'holes', for
   example if a zap has the following path nodes:
   ['Path B', 'Path C'], we need to make sure not to reuse 'B' or 'C'.
 */
const getNextPathValues = (usedValues: PathInfo[]): PathInfo => {
  const usedColors = _.map(usedValues, 'color');
  const unusedColorIndex = _.findIndex(
    PathColors,
    color => !_.find(usedColors, c => c === color.name)
  );

  // just start cycling over with first color if all are used:
  const colorIndex =
    unusedColorIndex === -1
      ? usedValues.length % PathColors.length
      : unusedColorIndex;

  const usedLetters = _.map(usedValues, 'emoji').sort();
  const lastLetter = _.last(usedLetters);
  const letter = nextLetter(lastLetter, usedValues.length);

  return {
    name: `Path ${letter}`,
    color: PathColors[colorIndex].name,
    emoji: letter,
  };
};

export const getPathAttrs = (node: SkinnyNode) => ({
  name: getPathName(node),
  color: getPathColor(node),
  emoji: getPathEmoji(node),
});

const getPathNodeAttrs = (pathValues, meta) => ({
  // For now, the type_of for path nodes is 'filter'.
  // This may change once the actual paths backend is implemented.
  type_of: 'filter',
  action: PATH_ACTION,
  selected_api: PATH_API,
  shouldRouteTo: false,
  params: {
    name: pathValues.name,
    emoji: pathValues.emoji,
    color: pathValues.color,
  },
  meta: {
    ...meta,
    stepTitle: pathValues.name,
  },
});

/*
   Create generator to get the next set of node attribute values to use
   for the next path node created in a zap. See getNextPathValues above.

   Returns a generator function so we can generate two path nodes in a row
   when creating a new path group. It's not a true ES generator a la *function
   just to avoid any browser compatibility issues. We presently don't need to
   run this in a for loop or anything.
*/
export const makePathNodeAttrsGenerator = (flow: Flow, meta?: {} = {}) => {
  const pathNodes = _.filter(_.values(flow.nodes), isPathNode);
  const usedValues = _.map(pathNodes, 'params');

  return () => {
    const nextValues = getNextPathValues(usedValues);
    usedValues.push(nextValues);
    return getPathNodeAttrs(nextValues, meta);
  };
};

// Node attributes for a new path group node
export const PATHS_GROUP_ATTRS = {
  type_of: 'write', // will eventually be 'paths_group' maybe?
  action: 'branch', // ditto ^^^
  selected_api: PATH_API,
};

// create a tree structure where sibling paths are on the same level
// and child paths are nested in a 'childPaths' property on the node.
// we are going backwards up the node tree
// so we don't have to check around the previous node
// to see if it's a sibling/parent and if so, add it to
// an array. Will create a structure like this:
/*

[
  {
    id: 1,
    parent_id: null,
    ...,
    childPaths: [
      {
        id: 2,
        parent_id: 1,
        ...
      },
      {
        id: 3,
        parent_id: 1,
        ...,
        childPaths: [
          {
            id: 4,
            parent_id: 3,
            ...,
          }
        ]
      },
    ],
  },
]

*/

export type PathNodeWithChildPaths = {
  ...$Exact<FlowNode>,
  childPaths?: PathNodeWithChildPaths[],
};

// TODO: This function is very similar to getRootNodeOfPath, except
// that this function returns null if currentNode is a path, or not in a
// path. Refactor to remove duplication.
export const findParentPathOfCurrentPath = (
  flowNodes: NodeMap,
  currentNode: FlowNode
): ?(PathNodeWithChildPaths | FlowNode) => {
  const pathParentId = getParentId(currentNode);
  const pathParent = pathParentId && flowNodes[pathParentId];
  if (pathParent && isPathNode(pathParent)) {
    return pathParent;
  } else if (pathParent) {
    return findParentPathOfCurrentPath(flowNodes, pathParent);
  }
  return null;
};

export const getBreadcrumbMenu = (
  pathNodes: PathNodeWithChildPaths[],
  flowNodes: NodeMap
) => {
  const nodesSortedByParentPath = _.reduceRight(
    pathNodes,
    (
      finalObj: { [id: string]: PathNodeWithChildPaths },
      currentNode: PathNodeWithChildPaths
    ) => {
      // traverse the tree to find the ancestor path of the current path
      const parentPath = findParentPathOfCurrentPath(flowNodes, currentNode);
      if (parentPath) {
        const existingSiblings = _.get(
          finalObj,
          [parentPath.id, 'childPaths'],
          []
        );
        // since we're working backwards, we may already have placed
        // this child in the tree as a top-level node. If that is the case,
        // use that node as the 'current node',
        // because it includes its own children.
        const maybeAlreadyPlacedChild = finalObj[currentNode.id];
        const childWithItsChildren = !maybeAlreadyPlacedChild
          ? currentNode
          : maybeAlreadyPlacedChild;
        finalObj[parentPath.id] = {
          ...parentPath,
          childPaths: [childWithItsChildren, ...existingSiblings],
        };
        // if the currentNode is already in the tree, delete it
        // because it will be a child node of the parent node
        // we are placing now.
        if (finalObj[currentNode.id]) {
          delete finalObj[currentNode.id];
        }
        // if the current node is a top level path node, we want
        // to include it in the finalObj if we haven't already.
        // this case happens for path nodes at the top level with no children.
      } else if (isPathNode(currentNode) && !finalObj[currentNode.id]) {
        finalObj[currentNode.id] = currentNode;
      }
      return finalObj;
    },
    {}
  );
  return _.values(nodesSortedByParentPath);
};

// TODO: drawPathConnectors is similar to drawVerticalPathConnectors.
// See if we can factor out into a reusable shared function, one that
// draws horizontally, and the other vertically, and with / without
// the leading curve, etc.
export const drawPathConnectors = ({
  numPaths,
  viewBoxHeight,
}: {
  numPaths: number,
  viewBoxHeight: number,
}) => {
  const sectionSize = viewBoxHeight / numPaths;
  const halfSection = sectionSize / 2;
  const lineHeight = viewBoxHeight / 2;
  const curveLen = viewBoxHeight / 10 / numPaths;

  // Don't draw any lines at all if there's only one path
  if (numPaths === 1) {
    return '';
  }

  // Create list of connector drawing instructions, one for each
  // path box. The first and the last connections are curved.
  // We accumulate the instructions into one big string.
  // The x value is accumulated as we move to the right
  // to draw the next connection.
  const connections = _.reduce(
    _.range(numPaths),
    ({ x, instructions }, i) => {
      const y =
        i === 0 || i === numPaths - 1 ? lineHeight + curveLen : lineHeight;
      return {
        instructions: instructions + `M ${x},${y} v ${lineHeight} `,
        x: x + sectionSize,
      };
    },
    { instructions: '', x: halfSection }
  ).instructions;

  const horizontalLine = `M ${halfSection +
    curveLen},${lineHeight} h ${sectionSize * (numPaths - 1) - curveLen * 2}`;
  const leftCurve = `M ${halfSection +
    curveLen},${lineHeight} q -${curveLen},0 -${curveLen},${curveLen}`;
  const rightCurve = `M ${halfSection +
    sectionSize * (numPaths - 1) -
    curveLen},${lineHeight} q ${curveLen},0 ${curveLen},${curveLen}`;

  return `${horizontalLine} ${connections} ${leftCurve} ${rightCurve}`;
};

// size of a path connectors curve
const CURVE_LEN = 5;

// a little space for the little top horizontal line in path connector svgs:
const SMIDGEON = 1;

/*
   Return svg path drawing instructions for a vertical set of connector lines.
   For example if numPaths is 3, the drawing will look something like this
   (but purtier):

   _
   |
   |--
   |
   |--
   |
   |__
 */
export const drawVerticalPathConnectors = ({
  numPaths,
  boxSize,
}: {
  boxSize: number,
  numPaths: number,
}) => {
  // Don't draw any lines at all if there's only one path:
  if (numPaths === 1) {
    return '';
  }

  const halfBoxSize = boxSize / 2;
  const vLineLen = numPaths * boxSize - halfBoxSize - CURVE_LEN;
  const hLineLen = boxSize;

  // list of y coordinates to start drawing the horizontal lines
  const hPositions = _.times(numPaths, i => (i + 1) * boxSize - halfBoxSize);

  // list of drawing instructions for each horizontal line:
  const hLines = hPositions
    .map((y, i) => {
      // on the last horizontal line we need to leave room for the curve
      const isLast = i === numPaths - 1;
      const len = isLast ? hLineLen - CURVE_LEN : hLineLen;
      const x = isLast ? boxSize + CURVE_LEN : boxSize;

      return `M ${x},${y} h ${len}`;
    })
    .join(' ');

  // purty curve connecting the vertical line and the last horizontal line:
  const curve = `M ${boxSize},${vLineLen} q 0,${CURVE_LEN} ${CURVE_LEN},${CURVE_LEN}`;

  const topLine = `M ${boxSize - CURVE_LEN},${SMIDGEON} h ${CURVE_LEN * 2}`;

  return `${topLine} M ${boxSize},${SMIDGEON} v ${vLineLen} ${hLines} ${curve}`;
};

const isDirectChildOfPathsGroup = (nodes: NodeMap, node) => {
  const parentId = getParentId(node);
  if (!parentId) {
    return false;
  }
  const parentNode = _.get(nodes, [parentId]);
  return parentNode && isPathsGroupNode(parentNode);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Given a node id, return the node for the first step of that node's
   path (which will be a 'path' node), or the root node of the zap if
   node is a top level node, not in a path.
 */
export const getRootNodeOfPath = (
  flowId: ID,
  nodes: NodeMap,
  nodeId?: ?ID
): FlowNode => {
  nodeId = nodeId || flowId;
  const rootNode = nodes[flowId];
  // if node has just been deleted, fall back to root node of zap
  const node = nodes[nodeId] || rootNode;

  // In some cases in production, the first step of a paths group
  // is _not_ a path node. We're not sure why this is happening,
  // but when it does happen it infinitely recurs, because it is
  // walking up to the trigger step, and starting over again...
  // As a workaround, do not depend on the selected_api, etc of
  // the supposed path node. Instead, check if the node's parent
  // is a paths group node.
  //
  // Note that findNodeUpwards will stop if the node has no parent
  // (ie is the trigger step), so we don't need to handle that case
  // here.
  const isFirstNodeOfPath = _.partial(isDirectChildOfPathsGroup, nodes);

  return (
    findNodeUpwards(nodes, node ? node.id : '', isFirstNodeOfPath) || rootNode
  );
};

/*
   Return true if two nodes are in the same path
 */
export const areInSamePath = (
  flowId: ID,
  nodes: NodeMap,
  nodeId1: ID,
  nodeId2: ID
) => {
  const rootNode1 = getRootNodeOfPath(flowId, nodes, nodeId1);
  const rootNode2 = getRootNodeOfPath(flowId, nodes, nodeId2);
  return _.get(rootNode1, 'id') === _.get(rootNode2, 'id');
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Gets the top level nodes that belong to one 'path', or the top level
   path of a zap, excluding nested paths / paths groups.

   Returns an array of nodes, sorted in step order.
 */
const EMPTY_NODES_FOR_PATH = [];
export const getNodesForPath = memoize(
  (flowId: ID, nodes: NodeMap, nodeId?: ID) => {
    // This function is called with task.zaps from TH, and I'm not 100% sure
    // that task.zaps is always defined. This replaces NodeUtils.sortNodes,
    // which returned [] if undefined, so preserve prior behavior to be safe.
    if (!nodes) {
      return EMPTY_NODES_FOR_PATH;
    }

    const rootNode = getRootNodeOfPath(flowId, nodes, nodeId);
    if (!rootNode) {
      return EMPTY_NODES_FOR_PATH;
    }

    return getNodeAndDescendants(nodes, rootNode.id, isPathsGroupNode);
  }
);

/*
   Given a node in a path, return the path group node that contains
   that path. Returns root node of zap if outside a path.
 */
export const getPathGroup = (flowId: ID, nodes: NodeMap, nodeId: ID) => {
  const rootNode = getRootNodeOfPath(flowId, nodes, nodeId);
  if (rootNode && rootNode.parent_id) {
    return nodes[rootNode.parent_id];
  }
  return rootNode;
};

/* Traverse up from current node till we find the first paths group */
export const getFirstPathsGroup = (nodes: NodeMap) => {
  const triggerNode = getTriggerNode(nodes);
  return triggerNode
    ? _.find(getDescendants(nodes, triggerNode.id), isPathsGroupNode)
    : undefined;
};

/*
   Return a step label for a node. For top level nodes, it
   returns the 1 based step number of the node: '1', '2', '3'.

   For nodes inside a path:
   showPathName = true, it prepends the path name: 'Path A - 2'.

   Used for the accordion gives dropdowns.
*/
export const getStepLabel = ({
  flowId,
  nodes,
  nodeId,
  options = { showPathName: true },
}: {
  flowId: ID,
  nodes: NodeMap,
  nodeId: ID,
  options?: { showPathName: boolean },
}) => {
  const { showPathName } = options;
  const rootNode = getRootNodeOfPath(flowId, nodes, nodeId);
  const stepNumber = getStepIndex(flowId, nodes, nodeId) + 1;

  if (isPathNode(rootNode) && showPathName) {
    return `${getPathName(rootNode)} - ${stepNumber}`;
  }
  return `${stepNumber}`;
};

/*
   Returns true if node is inside a path, false if a top level node.
*/
export const isNodeInPath = (flowId: ID, nodes: NodeMap, nodeId: ID) =>
  Boolean(getRootNodeOfPath(flowId, nodes, nodeId).parent_id);

/*
   Returns the current level of path nesting for a node.
     - 0: the top level of the zap
     - 1: the first path level,
     - 2: a path nested one level deep
     ...etc
*/
export const getCurrentNestingLevel = (
  flowId: ID,
  nodes: NodeMap,
  nodeId: ID,
  currentLevel: number = 0
) => {
  const rootNode = getPathGroup(flowId, nodes, nodeId);
  return isTriggerStep(rootNode) || !getParentId(rootNode)
    ? currentLevel
    : getCurrentNestingLevel(flowId, nodes, rootNode.id, currentLevel + 1);
};

export const isLastPathInGroup = (flow: Flow, maybePathNodeId: ID) => {
  const maybePathNode = flow.nodes[maybePathNodeId];
  if (!isPathNode(maybePathNode)) {
    return false;
  }

  const maybePathsGroupNodeId = getParentId(maybePathNode);
  if (!maybePathsGroupNodeId) {
    return false;
  }

  const maybePathsGroupNode = flow.nodes[maybePathsGroupNodeId];

  return (
    maybePathsGroupNode &&
    isPathsGroupNode(maybePathsGroupNode) &&
    getChildren(flow.nodes, maybePathsGroupNode.id).length === 1
  );
};

export const isNestedPath = (flowId: ID, nodes: NodeMap, nodeId: ID) =>
  getCurrentNestingLevel(flowId, nodes, nodeId) > 1;

export const isInPath = (flowId: ID, nodes: NodeMap, nodeId: ID) =>
  getCurrentNestingLevel(flowId, nodes, nodeId) > 0;

export const getNumberOfPathsAtCurrentLevel = (
  flowId: ID,
  nodes: NodeMap,
  nodeId: ID
) => {
  const pathsGroupNode = _.find(
    getNodesForPath(flowId, nodes, nodeId),
    isPathsGroupNode
  );
  if (!pathsGroupNode) {
    return 0;
  }
  return getChildren(nodes, pathsGroupNode.id).length;
};

const isUnlimited = limit => limit === -1;

export const hasReachedPathsDepthLimit = ({
  flowId,
  nodes,
  nodeId,
  pathsDepthLimit,
}: {
  flowId: ID,
  nodes: NodeMap,
  nodeId: ID,
  pathsDepthLimit: number,
}) =>
  !isUnlimited(pathsDepthLimit) &&
  getCurrentNestingLevel(flowId, nodes, nodeId) >= pathsDepthLimit;

export const hasReachedPathsWidthLimit = ({
  flowId,
  nodes,
  nodeId,
  pathsWidthLimit,
}: {
  flowId: ID,
  nodes: NodeMap,
  nodeId: ID,
  pathsWidthLimit: number,
}) =>
  !isUnlimited(pathsWidthLimit) &&
  getNumberOfPathsAtCurrentLevel(flowId, nodes, nodeId) >= pathsWidthLimit;

/*
   Returns an array of arrays of nodes, where each sub-array is an
   array of nodes for one path in the zap.
*/
export const getAllPaths = (
  flowId: ID,
  nodes: NodeMap,
  nodeId?: ID
): Array<Array<FlowNode>> => {
  const triggerNode = getTriggerNode(nodes);
  if (!triggerNode) {
    // I think this only happens in the unit tests!
    return [];
  }

  const nodesInCurrentPath = getNodesForPath(
    flowId,
    nodes,
    nodeId || triggerNode.id
  );
  const lastNodeInCurrentPath = _.last(nodesInCurrentPath);
  const currentResults = [nodesInCurrentPath];
  if (isPathsGroupNode(lastNodeInCurrentPath)) {
    const pathsGroupChildren = getChildren(nodes, lastNodeInCurrentPath.id);
    return currentResults.concat(
      _.flatten(
        pathsGroupChildren.map(node => getAllPaths(flowId, nodes, node.id))
      )
    );
  }
  return currentResults;
};

/*
   Get the last step of the current path, or the last step of the zap
   if at the top level.
 */
export const getLastStepOfPath = (flowId: ID, nodes: NodeMap, nodeId: ID) =>
  _.last(getNodesForPath(flowId, nodes, nodeId));

/*
   Returns true if node is the last step in a path.
 */
export const isLastStepOfPath = (flowId: ID, nodes: NodeMap, nodeId: ID) =>
  isInPath(flowId, nodes, nodeId) &&
  _.get(getLastStepOfPath(flowId, nodes, nodeId), 'id') === nodeId;

/*
   Return list of immediate path node children of the last
   step of this path, or top level of zap if not in a path.
 */
export const getPathNodes = (flowId: ID, nodes: NodeMap, nodeId: ID) => {
  const lastNode = getLastStepOfPath(flowId, nodes, nodeId);
  return lastNode && isPathsGroupNode(lastNode)
    ? getChildren(nodes, lastNode.id)
    : [];
};

/*
   Returns the root path node of the next sibling path to this path.
 */
export const getNextPath = (flowId: ID, nodes: NodeMap, nodeId: ID) => {
  const pathNode = getRootNodeOfPath(flowId, nodes, nodeId);
  if (!isPathNode(pathNode)) {
    return undefined;
  }

  const pathsGroupNodeId = getParentId(pathNode);
  if (!pathsGroupNodeId) {
    return undefined;
  }

  const pathsGroupNode = nodes[pathsGroupNodeId];
  const children = getChildren(nodes, pathsGroupNode.id);
  const currentIndex = _.findIndex(children, c => c.id === pathNode.id);
  return children[currentIndex + 1];
};

// Return true if every path underneath this path group is activatable and no steps are missing
export const areAllPathsActivatable = (
  flowId: ID,
  nodes: NodeMap,
  nodesSampleData: NodesSampleData,
  nodeId: ID,
  services: ServicesMap
) => {
  const pathsHaveCompletedSteps = _.isEmpty(
    getLastStepIsMiddleStepErrors(flowId, nodes, services)
  );

  const pathNodes = getNodeAndDescendants(nodes, nodeId);
  const areAllPathChildrenActivatable = pathNodes.every(node =>
    isActivatableNode(node, nodesSampleData, services)
  );

  return pathsHaveCompletedSteps && areAllPathChildrenActivatable;
};

export const hasPathsInZap = (nodes: NodeMap) => {
  return _.some(nodes, node => isPathsGroupNode(node) || isPathNode(node));
};

type NodeLevel = {
  nodeId: ID,
  level: number,
  pathId: ?ID,
};

type NodeLevels = Array<NodeLevel>;

const collectNodesByLevel = (
  rootNodeId,
  nodesMap,
  parentsToChildrenMap,
  level = 0,
  results = []
): NodeLevels => {
  const nodesInPath = getNodeAndDescendants(
    nodesMap,
    rootNodeId,
    isPathsGroupNode
  );
  const maybePathNode = _.first(nodesInPath);
  const pathId = isPathNode(maybePathNode) ? maybePathNode.id : undefined;
  nodesInPath.forEach(node => {
    results.push({
      nodeId: node.id,
      level,
      pathId,
    });
  });

  const lastNode = _.last(nodesInPath);
  if (isPathsGroupNode(lastNode)) {
    const childrenIds = parentsToChildrenMap[lastNode.id] || [];
    childrenIds.forEach(id =>
      collectNodesByLevel(
        id,
        nodesMap,
        parentsToChildrenMap,
        level + 1,
        results
      )
    );
  }

  return results;
};

export const getNodeLevels = (nodesMap: NodeMap): NodeLevels => {
  const rootNode = getRootNode(nodesMap);
  if (!rootNode) {
    return [];
  }
  const parentsToChildrenMap = getParentsToChildrenMap(nodesMap);
  return collectNodesByLevel(rootNode.id, nodesMap, parentsToChildrenMap);
};

type NodeStepNumbers = { [ID]: number };

const toNodeIdStepNumberPair = ({ nodeId }, index) => [nodeId, index + 1];

export const getNodeStepNumbers = (nodes: NodeMap): NodeStepNumbers => {
  const nodeLevels = getNodeLevels(nodes);
  const nodeIdStepNumberPairs = _.map(nodeLevels, toNodeIdStepNumberPair);
  return _.fromPairs(nodeIdStepNumberPairs);
};

export const isNodeDescedentOfPath = (
  currentNode: FlowNode,
  pathNode: FlowNode,
  flowNodes: NodeMap
) => {
  if (!currentNode || !pathNode || _.isEmpty(flowNodes)) {
    return false;
  }
  if (pathNode === currentNode) {
    return false;
  }
  const nodeAndDescendents = getNodeAndDescendants(flowNodes, pathNode.id);
  return nodeAndDescendents.includes(currentNode);
};
